// Builds a Motion Director plan into one scene as ONE undo step. Re-running on
// the same scene replaces the previous run (boxes whose id carries "-mdir-")
// instead of stacking duplicates. Targets the job's own navId — never the scene
// that happens to be selected when the author clicks "Apply to scene".

import { getDescendantBoxes } from "../../common/utils";
import { createBox } from "../../common/common_tools";
import {
  addBox,
  deleteBox,
  resizeBox,
  updateBoxAnimation,
  updateBoxExitAnimation,
  updateBoxTimeline,
  updatePluginToolbar,
} from "../../common/actions";
import { MDIR_ID_TAG, isAllowedAssetUrl, planTextMeasure, planToBoxSpecs } from "./motionImporter";

const MDIR_ID_RE = new RegExp(`-${MDIR_ID_TAG}-`);
// extract-stage element ids are x<image>-… (mw extract/build.ts)
const EXTRACTED_ID_RE = new RegExp(`-${MDIR_ID_TAG}-x\\d+-`);

/**
 * @param {Function} dispatch  editor store dispatch
 * @param {Function} getState  editor store getState
 * @param {Object} plan        validated Motion Director plan (job result `.plan`)
 * @param {{ navId: string, measure?: Function, imageSwaps?: {boxId: string, url: string}[], extractOnly?: boolean }} target
 *   the scene the job was run for; `measure` (motionImporter planTextMeasure) fits text to
 *   its box; `imageSwaps` are the cleaned (text-erased) images, the first url is kept in
 *   state.originalUrl (valid only while state.url === state.motionCleanUrl, i.e. the author
 *   has not replaced the image since); `extractOnly` (Sync with AI text extraction) replaces only the
 *   earlier extracted text/icon boxes, never a designed (Enrich & Motionize) layer
 * @returns {{ created: number, ids: string[], error?: "scene-missing" }}
 */
export function applyMotionPlan(
  dispatch,
  getState,
  plan,
  { navId, measure, imageSwaps = [], extractOnly = false }
) {
  const present = getState().undoGroup.present;
  const navItem = present.navItemsById && present.navItemsById[navId];
  if (!navItem || !plan) return { created: 0, ids: [], error: "scene-missing" };

  const specs = planToBoxSpecs(plan, { navId, measure });
  // reducers.jsx groupBy: every dispatch below collapses into one history entry.
  window.cropUndoGroup = `mdir-${Date.now()}`;
  try {
    const boxesById = present.boxesById || {};
    const toolbars = present.pluginToolbarsById || {};
    const setState = (id, name, value) =>
      dispatch(updatePluginToolbar(id, "main", "state", name, value));
    const swapped = new Set(imageSwaps.map((s) => s.boxId));
    // our swap is still showing: the author has not replaced the image since
    const ownSwap = (st) => !!(st && st.originalUrl && st.url === st.motionCleanUrl);
    // 1. restore images an earlier run swapped that this run does not (no text lost, none doubled);
    // a replaced image keeps what the author put there, only the stale markers go
    (navItem.boxes || []).forEach((id) => {
      const st = (toolbars[id] || {}).state;
      if (!st || !st.originalUrl || swapped.has(id)) return;
      if (ownSwap(st)) setState(id, "url", st.originalUrl);
      setState(id, "originalUrl", null);
      setState(id, "motionCleanUrl", null);
    });
    // 2. swap: keep the first original, show the cleaned image
    imageSwaps.forEach(({ boxId, url }) => {
      const tb = toolbars[boxId];
      if (!tb || !(navItem.boxes || []).includes(boxId) || !isAllowedAssetUrl(url)) return;
      const st = tb.state || {};
      if (!ownSwap(st)) setState(boxId, "originalUrl", st.url);
      setState(boxId, "url", url);
      setState(boxId, "motionCleanUrl", url);
    });
    (navItem.boxes || [])
      .filter((id) => (extractOnly ? EXTRACTED_ID_RE : MDIR_ID_RE).test(id))
      .forEach((existingId) => {
        const box = boxesById[existingId];
        if (!box) return;
        const descendants = getDescendantBoxes(box, boxesById);
        const cvs = [existingId, ...descendants].reduce(
          (acc, id) => acc.concat((boxesById[id] && boxesById[id].containedViews) || []),
          []
        );
        dispatch(deleteBox(existingId, box.parent, box.container, descendants, cvs, navId));
      });

    const ids = [];
    specs.forEach((spec) => {
      // Plain addBox (not addBoxThunk): the plan sets its own timeline, so the
      // playhead / shared-track placement must not run.
      createBox(
        spec.ids,
        spec.plugin,
        true,
        (...args) => dispatch(addBox(...args)),
        getState().undoGroup.present.boxesById,
        spec.style
      );
      if (!getState().undoGroup.present.boxesById[spec.id]) return; // plugin not registered
      dispatch(resizeBox(spec.id, spec.structure));
      dispatch(updateBoxTimeline(spec.id, spec.timeline));
      if (spec.enter)
        dispatch(updateBoxAnimation(spec.id, spec.enter.preset, spec.enter.duration, 0));
      if (spec.exit)
        dispatch(updateBoxExitAnimation(spec.id, spec.exit.preset, spec.exit.duration));
      ids.push(spec.id);
    });
    return { created: ids.length, ids };
  } finally {
    window.cropUndoGroup = null;
  }
}

/**
 * A finished Motion Director job's `result` ({ plan, imageSwaps, extractOnly }) on scene `navId`:
 * measures the text, then applyMotionPlan. Used by Sync with AI the moment its job completes and
 * by the job tray's Approve.
 * @returns {Promise<{ created: number, ids: string[], error?: "no-plan" | "scene-missing" }>}
 */
export async function applyMotionResult(dispatch, getState, result, navId) {
  const plan = result && result.plan;
  if (!plan || !Array.isArray(plan.elements)) return { created: 0, ids: [], error: "no-plan" };
  const measure = await planTextMeasure(plan).catch(() => undefined);
  return applyMotionPlan(dispatch, getState, plan, {
    navId,
    measure,
    imageSwaps: result.imageSwaps,
    extractOnly: !!result.extractOnly,
  });
}
